/**
 * PackOpenerSystem - Coordinates pack opening workflow with EventBus integration
 *
 * Orchestrates the multi-step pack opening workflow:
 * 1. Open pack -> display items -> user selects -> confirm purchase
 *
 * Part of Phase 5 system extraction. This follows the Strangler Fig pattern
 * established in Phase 3:
 * - PackOpenerSystem coordinates PackGenerator and WaveStateManager
 * - Adds EventBus integration for decoupled communication
 * - Enables testability through dependency injection
 *
 * Usage:
 *   import { PackOpenerSystem, getPackOpenerSystem } from './systems/PackOpenerSystem.js';
 *
 *   // Create instance with dependency injection
 *   const packOpenerSystem = new PackOpenerSystem(gameState, packGenerator);
 *
 *   // Or use singleton pattern
 *   const packOpenerSystem = getPackOpenerSystem(gameState, packGenerator);
 *
 *   // Open pack (emits PACK_OPENED event on success)
 *   const packData = packOpenerSystem.openPack(milestone);
 *
 *   // Select item (emits ITEM_SELECTED event on success)
 *   packOpenerSystem.selectItem(milestone, selectedItem);
 */

import { eventBus, GameEvents } from '../utils/EventBus.js';

// Add new events to GameEvents if not already present
if (!GameEvents.PACK_OPENED) {
    GameEvents.PACK_OPENED = 'pack-opened';           // { milestone, quality, wavedust, itemCount }
}
if (!GameEvents.ITEM_SELECTED) {
    GameEvents.ITEM_SELECTED = 'item-selected';       // { milestone, item }
}

export class PackOpenerSystem {
    /**
     * @param {GameState} gameState - Reference to main game state
     * @param {PackGenerator} packGenerator - Reference to pack generator
     */
    constructor(gameState, packGenerator) {
        this.gameState = gameState;
        this.packGenerator = packGenerator;
    }

    /**
     * Get the WaveStateManager from GameState
     * @returns {WaveStateManager}
     */
    get waveStateManager() {
        return this.gameState.waveStateManager;
    }

    /**
     * Open a pack at a given milestone
     *
     * First-time opens:
     * - Generates items via packGenerator
     * - Stores items in pack
     * - Deducts cost
     * - Awards wavedust
     * - Emits PACK_OPENED event
     *
     * Subsequent opens:
     * - Returns cached pack data (no regeneration)
     *
     * @param {number} milestone - The milestone identifier
     * @returns {object|false} Pack data with items, or false if failed
     */
    openPack(milestone) {
        // Find pack in available packs
        const pack = this.waveStateManager.getAvailablePacks().find(p => p.milestone === milestone);

        if (!pack) {
            return false;
        }

        // If pack already opened, return cached data (no regeneration)
        if (pack.opened) {
            return pack;
        }

        // Generate items for first-time open
        // Pass slotType for type-specific lots (slot-locked item generation)
        const generatedItems = this.packGenerator.generatePack(pack.packQuality, pack.slotType || null);

        // Delegate to WaveStateManager to handle cost deduction, wavedust, and storage
        const openedPack = this.waveStateManager.openPack(milestone, generatedItems);

        if (!openedPack) {
            return false;
        }

        // Emit PACK_OPENED event on success
        eventBus.emit(GameEvents.PACK_OPENED, {
            milestone: milestone,
            quality: openedPack.packQuality,
            wavedust: openedPack.wavedustEarned || 0,
            itemCount: openedPack.storedItems?.length || 0
        });

        return openedPack;
    }

    /**
     * Select an item from an opened pack and add to inventory
     *
     * - Adds selected item to inventory via gameState.addItem
     * - Confirms pack purchase (removes from available)
     * - Emits ITEM_SELECTED event on success
     *
     * @param {number} milestone - The milestone identifier
     * @param {object} selectedItem - The item selected by the user
     * @returns {boolean} True if successful
     */
    selectItem(milestone, selectedItem) {
        // Find pack to ensure it exists
        const pack = this.waveStateManager.getAvailablePacks().find(p => p.milestone === milestone);

        if (!pack) {
            return false;
        }

        // Add selected item to inventory
        this.gameState.addItem(selectedItem);

        // Confirm pack purchase (removes from available packs)
        const confirmed = this.waveStateManager.confirmPackPurchase(milestone);

        if (confirmed) {
            // Emit ITEM_SELECTED event on success
            eventBus.emit(GameEvents.ITEM_SELECTED, {
                milestone: milestone,
                item: selectedItem
            });
        }

        return confirmed;
    }

    /**
     * Get the pack tier name from quality value
     * @param {number} quality - Quality value (0.0 to 1.0)
     * @returns {string} Tier name
     */
    getPackTier(quality) {
        return this.packGenerator.getPackTier(quality);
    }
}

// =============================================================================
// SINGLETON PATTERN
// =============================================================================

let _instance = null;

/**
 * Get or create PackOpenerSystem singleton
 * @param {GameState} [gameState] - GameState reference (required on first call)
 * @param {PackGenerator} [packGenerator] - PackGenerator reference (required on first call)
 * @returns {PackOpenerSystem|null} PackOpenerSystem instance, or null if not initialized
 */
export function getPackOpenerSystem(gameState, packGenerator) {
    if (!_instance && gameState && packGenerator) {
        _instance = new PackOpenerSystem(gameState, packGenerator);
    }
    return _instance;
}

/**
 * Reset singleton instance (for testing)
 */
export function resetPackOpenerSystem() {
    _instance = null;
}
